import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiBolt, FiTarget, FiTrendingUp, FiBrain, FiAward } = FiIcons;

const BenefitsSection = () => {
  const benefits = [
    {
      icon: FiBolt,
      title: "Make Decisions 28% Faster",
      description: "Stop second-guessing. React with the lightning-fast tactical decisions that define elite performance.",
      color: "accent-magenta",
      bgColor: "bg-accent-magenta/10"
    },
    {
      icon: FiTarget,
      title: "Cut Motor Errors by 15% Under Pressure",
      description: "Execute your skills flawlessly when it matters most. No more choking in clutch moments.",
      color: "accent-gold",
      bgColor: "bg-accent-gold/10"
    },
    {
      icon: FiTrendingUp,
      title: "2.5x More Likely to Achieve Elite Status in Sport",
      description: "Join the ranks of champions who've mastered both physical and mental training.",
      color: "accent-purple",
      bgColor: "bg-accent-purple/10"
    },
    {
      icon: FiBrain,
      title: "Dominate the Mental Game",
      description: "Elite athletes show superior executive functions on key performance measures—we help you develop that same cognitive edge.",
      color: "accent-lavender",
      bgColor: "bg-accent-lavender/10"
    },
    {
      icon: FiAward,
      title: "Turn Pressure Into Power",
      description: "Transform high-stakes moments from your weakness into your greatest strength.",
      color: "accent-yellow",
      bgColor: "bg-accent-yellow/10"
    }
  ];

  return (
    <section id="benefits" className="py-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-playfair text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
            What Peak Mental Performance Actually Feels Like
          </h2>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className={`card hover:scale-105 transition-transform duration-300 ${benefit.bgColor} border-2 border-white`}
            >
              {/* Icon */}
              <div className="text-center mb-6">
                <div className={`inline-flex items-center justify-center w-16 h-16 bg-white rounded-full shadow-lg mb-4`}>
                  <SafeIcon icon={benefit.icon} className={`h-8 w-8 text-${benefit.color}`} />
                </div>
              </div>

              {/* Content */}
              <div className="text-center">
                <h3 className="font-playfair text-xl font-bold text-gray-900 mb-4 leading-tight">
                  {benefit.title}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional highlight */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mt-16"
        >
          <div className="bg-white p-8 lg:p-12 rounded-2xl shadow-lg border border-gray-100 text-center">
            <p className="text-xl text-gray-800 leading-relaxed max-w-4xl mx-auto">
              These aren't just improvements—they're the competitive advantages that separate good athletes from{' '}
              <span className="font-bold gradient-text">champions</span>.
            </p>
            <button
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="btn-primary text-lg mt-8"
            >
              Unlock Your Competitive Edge
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BenefitsSection;